import { useState } from 'react'
import {
    useTranslate,
    ArrayField,
    Datagrid,
    TextField,
    TopToolbar,
    useRefresh,
    FunctionField,
    useRecordContext
} from 'react-admin'
import { RaRecord } from 'ra-core'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Tooltip,
    Typography
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import _ from 'lodash'

import { formStyles } from 'layout/styles/formStyles'
import AutoCompleteSelectWithData from 'components/formfields/AutoCompleteSelectWithData'

import { useRetailersRetailersQuery } from 'apollo/retailer_manager/queries/RetailersRetailers.generated'
import { useCreateRetailerRepresentativeMutation } from 'apollo/contact_person_manager/mutations/CreateRetailerRepresentative.generated'
import { useDeleteRetailerRepresentativeMutation } from 'apollo/contact_person_manager/mutations/DeleteRetailerRepresentative.generated'

const RetailerRepresentativeRetailerList = (props) => {
    const sx = {
        dialogActions: {
            padding: 0,
            paddingbottom: '1rem'
        },
        deleteIcon: {
            cursor: 'pointer',
            '&:hover': {
                transform: `scale(1.1)`
            }
        },
        spinner: {
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: '-12px',
            marginLeft: '-12px'
        },
        datagridLoading: {
            opacity: 0.5,
            cursor: 'progress',
            pointerevents: 'none'
        }
    }
    const translate = useTranslate()
    const reload = useRefresh()

    const [open, setOpen] = useState(false)
    const [selectedRetailerID, setSelectedRetailerID] = useState<number | null>(null)

    const [
        createRetailerRepresentativeMutation,
        { loading: isLoadingCreateRetailerRepresentativeMutation }
    ] = useCreateRetailerRepresentativeMutation()
    const [
        deleteRetailerRepresentativeMutation,
        { loading: isLoadingDeleteRetailerRepresentativeMutation }
    ] = useDeleteRetailerRepresentativeMutation()

    const { data: retailers = {} } = useRetailersRetailersQuery({
        variables: {
            first: 10000
        },
        fetchPolicy: 'cache-and-network'
    })

    const record = useRecordContext()
    if (!record) return null

    const togglePopup = (): void => {
        setOpen(!open)
    }

    const handleRetailerAutocompleteChange = (selectedItem) => {
        setSelectedRetailerID(Number(selectedItem))
    }

    const handlePopupSubmit = () => {
        if (selectedRetailerID)
            createRetailerRepresentativeMutation({
                variables: {
                    representativeId: +record?.id,
                    retailerId: selectedRetailerID
                }
            })
                .then(() => {
                    setOpen(false)
                    setSelectedRetailerID(null)
                    reload()
                })
                .catch((error) => {
                    console.error(error)
                })
    }

    const handleRetailerDeleteClick = (event, id) => {
        event.stopPropagation()
        deleteRetailerRepresentativeMutation({
            variables: {
                representativeId: +record?.id,
                retailerId: id
            }
        })
            .then(() => {
                reload()
            })
            .catch((error) => {
                console.error(error)
            })
    }

    const filteredRetailers = _.differenceBy(
        retailers?.retailersRetailers?.data,
        record?.retailers,
        'id'
    )

    return (
        <>
            <TopToolbar>
                <Button color="primary" variant="outlined" onClick={togglePopup}>
                    <AddIcon />
                    {translate(`manager.resources.contactPersons.add_retailer`)}
                </Button>
            </TopToolbar>

            <Grid container spacing={1} sx={formStyles.fullWidth} style={{ marginBottom: 30 }}>
                <Grid item md={6} style={{ position: 'relative' }}>
                    <ArrayField source="retailers">
                        <Datagrid
                            sx={
                                isLoadingDeleteRetailerRepresentativeMutation
                                    ? sx.datagridLoading
                                    : {}
                            }
                            rowClick={(_, __, record) => `/Retailer/${record.id}`}
                        >
                            <TextField
                                source="name"
                                label={translate('manager.resources.contactPersons.retailer_name')}
                            />
                            <FunctionField
                                label={translate('manager.resources.general.actions')}
                                render={(record: RaRecord) =>
                                    record?.id && (
                                        <Tooltip
                                            title={translate('manager.resources.general.delete')}
                                        >
                                            <DeleteIcon
                                                sx={sx.deleteIcon}
                                                onClick={(event) =>
                                                    handleRetailerDeleteClick(event, +record?.id)
                                                }
                                            />
                                        </Tooltip>
                                    )
                                }
                            />
                        </Datagrid>
                    </ArrayField>
                    {isLoadingDeleteRetailerRepresentativeMutation && (
                        <CircularProgress sx={sx.spinner} />
                    )}
                </Grid>
            </Grid>

            <Dialog fullWidth open={open} onClose={togglePopup}>
                <DialogTitle>
                    <Typography variant="h6" gutterBottom>
                        {translate('manager.resources.contactPersons.add_retailer')}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <AutoCompleteSelectWithData
                        required
                        format
                        source="retailers"
                        data={filteredRetailers ?? []}
                        onChange={handleRetailerAutocompleteChange}
                        disabled={filteredRetailers.length === 0}
                    />
                    <DialogActions sx={sx.dialogActions}>
                        <Button onClick={togglePopup} variant="contained" color="primary">
                            {translate('manager.resources.packageGroup.package.close')}
                        </Button>
                        <Button
                            onClick={handlePopupSubmit}
                            variant="contained"
                            color="primary"
                            disabled={
                                !selectedRetailerID || isLoadingCreateRetailerRepresentativeMutation
                            }
                        >
                            {translate('manager.resources.packageGroup.package.add_button')}
                            {isLoadingCreateRetailerRepresentativeMutation && (
                                <CircularProgress size={24} sx={sx.spinner} />
                            )}
                        </Button>
                    </DialogActions>
                </DialogContent>
            </Dialog>
        </>
    )
}

export default RetailerRepresentativeRetailerList
